import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import { homeRoute, routeAllowed } from '@/router/navigation'
import { installLoadRecovery } from '@/router/loadRecovery'
import { useAuth } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    /** Заглавие на раздела в браузъра. */
    title: string
    /** Достъпна без вход. */
    public?: boolean
    /** „bare" — без обвивката на приложението: входът и листовете за печат. */
    layout?: 'app' | 'bare'
    /** Страницата слага свои раздели в горната лента (нарядът: Наряд · Попълнено). */
    ownTabs?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/popalvane',
    name: 'intake',
    component: () => import('@/views/naryadi/IntakeView.vue'),
    meta: { title: 'Попълване на наряд' },
  },
  {
    path: '/naryadi/obekti',
    name: 'site-naryads',
    component: () => import('@/views/naryadi/SiteNaryadListView.vue'),
    meta: { title: 'Наряди' },
  },
  {
    path: '/naryadi/obekti/:id(\\d+)',
    name: 'site-naryad',
    component: () => import('@/views/naryadi/SiteNaryadView.vue'),
    props: true,
    meta: { title: 'Наряд' },
  },
  {
    path: '/potrebiteli',
    name: 'users',
    component: () => import('@/views/settings/UserListView.vue'),
    meta: { title: 'Потребители' },
  },
  {
    path: '/otcheti',
    name: 'site-reports',
    component: () => import('@/views/sitereports/SiteReportListView.vue'),
    meta: { title: 'Отчети от обектите' },
  },
  {
    path: '/otcheti/:id(\\d+)',
    name: 'site-report',
    component: () => import('@/views/sitereports/SiteReportView.vue'),
    props: true,
    meta: { title: 'Отчет' },
  },
  {
    path: '/skanirane',
    name: 'scan-documents',
    component: () => import('@/views/documents/ScanUploadView.vue'),
    meta: { title: 'Сканиране на документи' },
  },
  {
    path: '/skanirane/odobrenie',
    name: 'scan-approvals',
    component: () => import('@/views/documents/ScanQueueView.vue'),
    meta: { title: 'Одобрение на документи' },
  },
  {
    path: '/skanirane/odobrenie/:id(\\d+)',
    name: 'scan-review',
    component: () => import('@/views/documents/ScanReviewView.vue'),
    props: true,
    meta: { title: 'Преглед на документ' },
  },
  {
    path: '/skanirane/skanirani',
    name: 'scanned-files',
    component: () => import('@/views/documents/ScannedFilesView.vue'),
    meta: { title: 'Сканирани' },
  },
  {
    path: '/skanirane/failove',
    name: 'all-files',
    component: () => import('@/views/documents/AllFilesView.vue'),
    meta: { title: 'Всички файлове' },
  },
  {
    path: '/vhod',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: 'Вход', public: true, layout: 'bare' },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { title: 'Табло' },
  },
  {
    path: '/naryadi',
    name: 'naryads',
    component: () => import('@/views/naryadi/NaryadListView.vue'),
    meta: { title: 'Наряди' },
  },
  {
    path: '/naryadi/:id(\\d+)/:tab(popalneno)?',
    name: 'naryad',
    component: () => import('@/views/naryadi/NaryadDetailView.vue'),
    props: true,
    // Своите раздели (Наряд · Попълнено) — в горната лента вместо тези на „Наряди“.
    meta: { title: 'Наряд', ownTabs: true },
  },
  {
    path: '/naryadi/:id(\\d+)/popalneno/:blockId(\\d+)',
    name: 'intake-review',
    component: () => import('@/views/naryadi/IntakeReviewView.vue'),
    props: true,
    meta: { title: 'Преглед на попълнено' },
  },
  {
    path: '/naryadi/:id(\\d+)/plashtane/novo',
    name: 'naryad-payment-new',
    component: () => import('@/views/naryadi/NaryadPaymentEditorView.vue'),
    props: true,
    meta: { title: 'Добави плащане' },
  },
  {
    path: '/naryadi/:id(\\d+)/plashtane/:entryId(\\d+)',
    name: 'naryad-payment-draft',
    component: () => import('@/views/naryadi/NaryadPaymentEditorView.vue'),
    props: true,
    meta: { title: 'Чернова на плащане' },
  },
  {
    path: '/naryadi/:id(\\d+)/kontragent/:entryId(\\d+)',
    name: 'naryad-entry',
    component: () => import('@/views/naryadi/NaryadEntryView.vue'),
    props: true,
    meta: { title: 'Плащане по наряд' },
  },
  {
    path: '/plashtania',
    name: 'payments',
    component: () => import('@/views/payments/PaymentsListView.vue'),
    meta: { title: 'Плащания' },
  },
  {
    path: '/naryadi/avansi',
    name: 'advances',
    component: () => import('@/views/naryadi/AdvanceListView.vue'),
    meta: { title: 'Аванси' },
  },
  {
    path: '/zaemi',
    name: 'loans',
    component: () => import('@/views/loans/LoanListView.vue'),
    meta: { title: 'Заеми' },
  },
  {
    // kontragent/sluzhitel — по id; ime — по написаното име.
    path: '/zaemi/:kind(kontragent|sluzhitel|ime)/:payee',
    name: 'loan',
    component: () => import('@/views/loans/LoanDetailView.vue'),
    props: true,
    meta: { title: 'Заем' },
  },
  // Старият модел (`workorders`). Данните му са изтрити 02.10.2026 и няма
  // връзка към него от менюто; маршрутите остават само докато картоните на
  // обект и контрагент още ги назовават.
  {
    path: '/naryadi/arhiv',
    name: 'work-orders',
    component: () => import('@/views/workorders/WorkOrderListView.vue'),
    meta: { title: 'Архив на нарядите' },
  },
  {
    path: '/naryadi/arhiv/nov',
    name: 'work-order-new',
    component: () => import('@/views/workorders/WorkOrderEditorView.vue'),
    meta: { title: 'Нов наряд в архива' },
  },
  {
    path: '/naryadi/arhiv/:id(\\d+)',
    name: 'work-order',
    component: () => import('@/views/workorders/WorkOrderEditorView.vue'),
    props: true,
    meta: { title: 'Наряд от архива' },
  },
  {
    path: '/dogovori',
    name: 'contracts',
    component: () => import('@/views/contracts/ContractListView.vue'),
    meta: { title: 'Работи' },
  },
  {
    path: '/dogovori/:id(\\d+)',
    name: 'contract',
    component: () => import('@/views/contracts/ContractDetailView.vue'),
    props: true,
    meta: { title: 'Работа' },
  },
  {
    // Доставки, материали и наряди са една страница; старите връзки водят там.
    path: '/obekti/:id(\\d+)/dostavki',
    redirect: (to) => ({ name: 'project-expenses', params: to.params, query: to.query }),
  },
  {
    path: '/materiali/obekti/:id(\\d+)',
    redirect: (to) => ({ name: 'project-expenses', params: to.params, query: to.query }),
  },
  {
    path: '/obekti/:id(\\d+)/byudzhet',
    name: 'project-budget',
    component: () => import('@/views/projects/ProjectBudgetView.vue'),
    props: true,
    meta: { title: 'Бюджет на обекта' },
  },
  {
    path: '/obekti/:id(\\d+)/naryadi',
    name: 'project-naryadi',
    component: () => import('@/views/projects/ProjectNaryadiView.vue'),
    props: true,
    meta: { title: 'Наряди по обекта' },
  },
  {
    path: '/obekti/:id(\\d+)/kasa',
    name: 'project-ledger',
    component: () => import('@/views/projects/ProjectLedgerView.vue'),
    props: true,
    meta: { title: 'Каса по обекта' },
  },
  {
    path: '/faktury',
    name: 'invoices',
    component: () => import('@/views/invoicing/InvoiceListView.vue'),
    meta: { title: 'Фактури' },
  },
  {
    path: '/faktury/anulirani',
    name: 'invoices-cancelled',
    component: () => import('@/views/invoicing/InvoiceListView.vue'),
    props: { cancelled: true },
    meta: { title: 'Анулирани фактури' },
  },
  {
    path: '/faktury/:id(\\d+)',
    name: 'invoice',
    component: () => import('@/views/invoicing/InvoiceDetailView.vue'),
    props: true,
    meta: { title: 'Фактура' },
  },
  {
    path: '/materiali',
    name: 'materials',
    component: () => import('@/views/inventory/MaterialPurchasesView.vue'),
    meta: { title: 'Материали' },
  },
  {
    path: '/razhodi/obekti/:id(\\d+)',
    name: 'project-expenses',
    component: () => import('@/views/inventory/ProjectMaterialPurchasesView.vue'),
    props: true,
    meta: { title: 'Разходи по обекта' },
  },
  {
    path: '/sluzhiteli/rabotno-vreme',
    name: 'employee-work-schedule',
    component: () => import('@/views/hr/WorkScheduleView.vue'),
    meta: { title: 'График на служителите' },
  },
  {
    path: '/sluzhiteli',
    name: 'employees',
    component: () => import('@/views/hr/EmployeeListView.vue'),
    meta: { title: 'Служители' },
  },
  {
    path: '/sluzhiteli/:id(\\d+)',
    name: 'employee',
    component: () => import('@/views/hr/EmployeeView.vue'),
    props: true,
    meta: { title: 'Служител' },
  },
  {
    path: '/sluzhiteli/brigadi',
    name: 'crews',
    component: () => import('@/views/hr/CrewListView.vue'),
    meta: { title: 'Бригади' },
  },
  {
    path: '/sluzhiteli/umenia',
    name: 'skills',
    component: () => import('@/views/hr/CrewListView.vue'),
    props: { tab: 'skills' },
    meta: { title: 'Умения' },
  },
  {
    path: '/sluzhiteli/grafik',
    name: 'crew-schedule',
    component: () => import('@/views/hr/CrewScheduleView.vue'),
    meta: { title: 'График на бригадите' },
  },
  {
    path: '/za-plashtane',
    name: 'payment-run',
    component: () => import('@/views/payments/PaymentRunView.vue'),
    meta: { title: 'За плащане' },
  },
  {
    path: '/dds',
    name: 'vat',
    component: () => import('@/views/vat/VatView.vue'),
    meta: { title: 'ДДС' },
  },
  {
    path: '/kasa',
    name: 'ledger',
    component: () => import('@/views/ledger/TransactionListView.vue'),
    meta: { title: 'Каса' },
  },
  {
    // Бързото въвеждане покрива седмичната рутина, но едно движение с няколко
    // разпределения се нуждае от целия формуляр.
    path: '/kasa/novo',
    name: 'transaction-new',
    component: () => import('@/views/ledger/TransactionEditorView.vue'),
    meta: { title: 'Ново движение' },
  },
  {
    path: '/kasa/:id(\\d+)',
    name: 'transaction',
    component: () => import('@/views/ledger/TransactionEditorView.vue'),
    props: true,
    meta: { title: 'Движение' },
  },
  {
    path: '/kasa/:id(\\d+)/order',
    name: 'cash-order',
    component: () => import('@/views/ledger/CashOrderPrintView.vue'),
    props: true,
    meta: { title: 'Касов ордер', layout: 'bare' },
  },
  {
    path: '/obekti',
    name: 'projects',
    component: () => import('@/views/projects/ProjectListView.vue'),
    meta: { title: 'Обекти' },
  },
  {
    path: '/grafik',
    name: 'work-schedule',
    component: () => import('@/views/projects/WorkScheduleView.vue'),
    meta: { title: 'График на работата' },
  },
  {
    path: '/obekti/nov',
    name: 'project-new',
    component: () => import('@/views/projects/NewProjectView.vue'),
    meta: { title: 'Нов обект' },
  },
  {
    // Преди „/obekti/:id/:tab?“, за да не стане „dati“ име на таб.
    path: '/obekti/:id(\\d+)/dati',
    name: 'project-act-dates',
    component: () => import('@/views/projects/ProjectActDatesView.vue'),
    props: true,
    meta: { title: 'Датите на актовете' },
  },
  {
    path: '/obekti/:id(\\d+)/:tab?',
    name: 'project',
    component: () => import('@/views/projects/ProjectDetailView.vue'),
    props: true,
    meta: { title: 'Обект' },
  },
  {
    path: '/kontragenti',
    name: 'counterparties',
    component: () => import('@/views/directory/CounterpartyListView.vue'),
    meta: { title: 'Контрагенти' },
  },
  // Бригадите са от наши служители — старите адреси водят натам.
  { path: '/kontragenti/brigadi', redirect: '/sluzhiteli/brigadi' },
  { path: '/kontragenti/umenia', redirect: '/sluzhiteli/umenia' },
  {
    path: '/kontragenti/zaetost',
    name: 'occupancy',
    component: () => import('@/views/directory/OccupancyView.vue'),
    meta: { title: 'Заетост на контрагентите' },
  },
  {
    path: '/kontragenti/svarzvane',
    name: 'curation',
    component: () => import('@/views/directory/CurationView.vue'),
    meta: { title: 'Свързване на имена' },
  },
  {
    // Пътят с номер стои след „/kontragenti/svarzvane“, за да не го поглъща.
    path: '/kontragenti/:id(\\d+)',
    name: 'counterparty',
    component: () => import('@/views/directory/CounterpartyDetailView.vue'),
    props: true,
    meta: { title: 'Контрагент' },
  },
  {
    path: '/vizitki',
    name: 'business-cards',
    component: () => import('@/views/directory/BusinessCardListView.vue'),
    meta: { title: 'Визитки' },
  },
  {
    path: '/druzhestva',
    name: 'companies',
    component: () => import('@/views/companies/CompanyListView.vue'),
    meta: { title: 'Дружества' },
  },
  {
    path: '/spravki',
    name: 'reports',
    component: () => import('@/views/ReportsView.vue'),
    meta: { title: 'Справки' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Страницата не е намерена' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

const APP_NAME = 'Изолации ЕООД'

installLoadRecovery(router)

router.beforeEach(async (to) => {
  const auth = useAuth()
  // Сесията се проверява веднъж на зареждане: презареденият таб трябва да
  // остане вътре, ако бисквитката още е валидна.
  await auth.ensureResolved()

  if (to.meta.public) {
    return auth.isAuthenticated && to.name === 'login' ? { name: 'dashboard' } : true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }

  // Менюто крие недостъпното; адресът, написан на ръка, води към началото.
  if (!routeAllowed(to.name, auth)) {
    const home = homeRoute(auth)
    return home && home !== to.name ? { name: home } : { name: 'not-found', params: { pathMatch: to.path.slice(1).split('/') } }
  }

  return true
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · ${APP_NAME}` : APP_NAME
})

export default router
